import React, { useState } from 'react';
import {
  FaShieldAlt, FaChartLine, FaCheckCircle, FaChevronDown,
  FaBuilding, FaHandHoldingUsd, FaFileContract, FaCoins,
  FaBalanceScale, FaSearchDollar
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const PrivateCredit = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Private Credit?",
      a: "Private Credit refers to privately negotiated loans and debt financing provided by non-bank lenders (like Private Credit Funds) to companies. These loans are not traded on public markets and offer bespoke terms, often yielding higher returns than traditional corporate bonds."
    },
    {
      q: "How does Private Credit differ from traditional Fixed Income?",
      a: "Unlike public bonds or bank fixed deposits, Private Credit is illiquid and typically features floating interest rates, senior secured positioning, and stronger financial covenants. Investors accept a liquidity lock-in in exchange for a significant 'illiquidity premium' (higher yields)."
    },
    {
      q: "Is my capital protected in Private Credit investments?",
      a: "While no investment is entirely risk-free, Private Credit strategies heavily focus on capital preservation. Loans are frequently 'Senior Secured,' meaning they are backed by the borrowing company's hard assets (real estate, inventory, receivables) and rank highest in the capital structure for repayment."
    },
    {
      q: "Who is eligible to invest in Private Credit funds?",
      a: "In India, Private Credit is primarily accessed through Category II Alternative Investment Funds (AIFs). As per SEBI regulations, this requires a minimum investment of ₹1 Crore, making it suitable for HNIs, Ultra-HNIs, Family Offices, and institutional investors."
    }
  ];

  return (
    <main className="private-credit-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Private Credit Investment Services in Hyderabad | Private Debt Investments | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth offers Private Credit Investment Services in Hyderabad, providing access to private debt funds, corporate credit, structured credit solutions, fixed income investments, and alternative lending opportunities designed for stable income and long-term wealth creation."
        />

        <meta
          name="keywords"
          content="Private Credit Hyderabad, Private Credit Investment Hyderabad, Private Credit Services Hyderabad, Private Debt Investments Hyderabad, Private Debt Funds Hyderabad, Corporate Credit Hyderabad, Direct Lending Hyderabad, Structured Credit Hyderabad, Fixed Income Investments Hyderabad, Alternative Credit Hyderabad, Alternative Investments Hyderabad, Wealth Management Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Asset Management Hyderabad, High Net Worth Investments Hyderabad, HNI Investment Hyderabad, Portfolio Diversification Hyderabad, Investment Planning Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/private-credit" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/private-credit" />
        <meta
          property="og:title"
          content="Private Credit Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Invest in private credit opportunities with Lotoswealth. Access private debt funds, direct lending, structured credit, fixed income investments, and alternative credit solutions in Hyderabad."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Private Credit Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Generate stable returns through private credit investments with Lotoswealth. Explore private debt funds, direct lending, structured credit, and fixed income opportunities in Hyderabad."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/private-credit.jpg"
            alt="Private Credit Advisory"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Private Credit Advisory
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Alternative Fixed Income for Superior Yields
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. In an environment where traditional fixed-income returns often struggle to outpace inflation, Private Credit has emerged as a cornerstone asset class for sophisticated investors seeking elevated, consistent, and predictable cash flows.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            By providing bespoke debt capital directly to robust, mid-market enterprises outside of the traditional banking system, investors can capture an "illiquidity premium" while benefiting from stringent structural protections and asset collateralization.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Explore institutional-grade private debt strategies that prioritize capital preservation, shield your portfolio from equity market volatility, and generate steady, high-yielding returns.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY PRIVATE CREDIT MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Consider Private Credit?
              </h2>
              <p className="text-muted line-height-lg">
                In times of rising interest rates and equity market turbulence, Private Credit acts as an essential portfolio stabilizer. It offers returns that are contractually mandated, unlike corporate dividends which can be slashed during economic downturns.
              </p>
              <p className="text-muted line-height-lg">
                Furthermore, private debt instruments are often structured with floating interest rates, providing a natural hedge against inflation. Backed by stringent financial covenants and tangible collateral, Private Credit minimizes downside risk while maximizing income generation.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">The Private Debt Advantage</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Higher Yields:</strong> Outperform traditional bonds and fixed deposits.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Capital Preservation:</strong> Senior secured positions with asset collateral.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Low Correlation:</strong> Immune to the daily volatility of public stock markets.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Consistent Cash Flow:</strong> Predictable, contractual interest distributions.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR PRIVATE CREDIT OFFERINGS (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💼 Our Private Credit Strategies</h2>
            <p className="text-white">Curated non-banking debt opportunities for sophisticated capital allocators.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Senior Secured Debt</h5>
                <p className="text-muted small">Invest in loans that sit at the top of the capital structure. These are heavily collateralized by company assets, ensuring the highest priority of repayment and maximum capital safety.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBalanceScale size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Mezzanine Financing</h5>
                <p className="text-muted small">A hybrid of debt and equity financing. Subordinated to senior debt but offering higher target yields and potential equity kickers (warrants) for enhanced upside.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Venture Debt</h5>
                <p className="text-muted small">Provide non-dilutive capital to high-growth, venture-backed startups. Gain attractive interest yields along with equity upside without standard venture capital risks.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBuilding size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Real Estate Credit</h5>
                <p className="text-muted small">Participate in specialized funds offering debt capital to tier-1 real estate developers. Backed by hard physical assets and project cash flows for robust security.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHandHoldingUsd size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Distressed & Special Situations</h5>
                <p className="text-muted small">Invest in the debt of fundamentally strong companies undergoing temporary liquidity crunches, allowing for deeply discounted entry points and high recovery potentials.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCoins size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Performing Credit AIFs</h5>
                <p className="text-muted small">Access diversified portfolios of corporate loans managed by elite SEBI-registered Category II AIF managers, designed to generate consistent quarterly payouts.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP DUE DILIGENCE PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Credit Evaluation Process</h2>
            <p className="text-light opacity-75">Strict underwriting standards to protect your principal capital.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Deal Origination</h5>
                <p className="small text-muted mb-0">We collaborate with premier Credit AIFs and institutional lenders to source exclusive debt opportunities across diverse sectors.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Credit Underwriting</h5>
                <p className="small text-muted mb-0">Deep dive into the borrower's cash flows, balance sheets, leverage ratios, and enterprise valuation to ensure repayment capacity.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Collateral & Covenants</h5>
                <p className="small text-muted mb-0">Ensuring the debt is backed by hard assets (2x-3x cover) and strictly enforcing financial covenants to act as early warning signals.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Portfolio Integration</h5>
                <p className="small text-muted mb-0">Strategically allocating Private Credit within your broader portfolio to elevate overall yield while managing duration and illiquidity risks.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Active Monitoring</h5>
                <p className="small text-muted mb-0">Continuously tracking borrower performance, ensuring timely interest payouts, and proactively managing exit or refinancing events.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Should Invest in Private Credit?</h3>
              <p className="text-muted small mb-4">High-yield debt strategies are ideally suited for:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Yield-Seeking HNIs & UHNIs", "Family Offices",
                  "Corporate Treasuries", "Retirees Seeking Fixed Income",
                  "Accredited Investors", "Investors with Patient Capital",
                  "Conservative Wealth Preservers", "Portfolio Diversifiers"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Lotoswealth for Debt Markets?</h3>
                <p className="text-muted small mb-4">Evaluating private debt requires specialized credit analysis expertise. We offer our clients:</p>
                <div className="row g-3">
                  {[
                    "Access to Top-Tier Credit AIFs", "Strict Focus on Senior Secured Debt",
                    "Rigorous Credit Default Analysis", "Floating Rate Strategies for Inflation",
                    "Regular Yield Distribution Models", "Transparent Fee & Cost Structures",
                    "Dedicated Alternative Debt Experts", "Robust Collateral Auditing"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Private Credit & High-Yield Debt</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Enhance Your Portfolio's Income Generation</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Move beyond traditional fixed deposits and volatile equities. Discover how Private Credit can deliver stable, elevated yields for your wealth.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Speak to a Private Credit Expert
          </button>
        </div>
      </section>

    </main>
  );
};

export default PrivateCredit;